import React, { useState, useEffect, type ReactNode } from 'react';
import styled from 'styled-components';

import { MAX_NUM_PAGES } from './constants';
import { PaginationUI } from './PaginationUI';
import { StyledPaginationContainer } from './StyledPaginationContainer';
import { ConditionalRendering } from '../conditional-rendering';

const ROWS_PER_PAGE = [5, 10, 20, 30, 40, 50];
const PAGINATION_NUM_PAGES_DEFAULT = Array.from({ length: MAX_NUM_PAGES }).map((_, i) => i + 1);

// Styled Components

interface StyledDataRangeProps {
    mt?: number;
    children: ReactNode;
}

const StyledDataRange = styled.p<StyledDataRangeProps>`
    color: ${({ theme }) => theme.gray2};
    margin-top: ${({ mt }) => (mt ? `${mt}px` : '0')};
`;

const StyledBox = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const StyledSelect = styled.select<{ children: ReactNode; value: number; onChange: (e: any) => void }>`
    padding: 4px 8px;
`;

interface ServerSidePaginationProps {
    onPagination: (page: number, pageSize: number) => Promise<void>;
    setVisiblePages: (pages: number[]) => void;
    totalCount: number;
    rowsPerPage: number;
    visiblePageNumbers: number;
    margin?: number;
    isShowPaginationHeader?: boolean;
    selectedPage?: number;
    pageNumbersToShow?: number[];
    isShowRowsPerPage?: boolean;
    prevText?: string;
    nextText?: string;
    rowsPerPageOptions?: number[];
    isFirstAndLastNavigation?: boolean;
}

function ServerSidePagination({
    onPagination,
    setVisiblePages,
    totalCount,
    rowsPerPage,
    visiblePageNumbers,
    margin = 4,
    isShowPaginationHeader,
    selectedPage = 1,
    pageNumbersToShow = PAGINATION_NUM_PAGES_DEFAULT,
    isShowRowsPerPage = false,
    prevText,
    nextText,
    rowsPerPageOptions = ROWS_PER_PAGE,
    isFirstAndLastNavigation,
}: ServerSidePaginationProps) {
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(rowsPerPage);
    const [currentPage, setCurrentPage] = useState(() => selectedPage);

    useEffect(() => {
        setPageDetails();
    }, [pageSize, totalCount, visiblePageNumbers]);

    const setPageDetails = () => {
        const noOfPages = Math.ceil(totalCount / pageSize);
        setTotalPages(noOfPages);

        if (noOfPages > 0) {
            let startPage = Math.max(1, currentPage - Math.floor(visiblePageNumbers / 2));
            let endPage = Math.min(noOfPages, startPage + visiblePageNumbers - 1);
            startPage = Math.max(1, endPage - visiblePageNumbers + 1);
            const pagesToShow: number[] = [];
            for (let i = startPage; i <= endPage; i++) {
                pagesToShow.push(i);
            }
            setVisiblePages(pagesToShow);
        }

        if (currentPage > noOfPages) {
            setCurrentPage(currentPage - 1);
        }
    };

    const onPageChange = async (page: number) => {
        setCurrentPage(page);
        await onPagination(page, pageSize);
    };

    const goToFirstPage = async () => {
        setCurrentPage(1);
        await onPagination(1, pageSize);
    };

    const goToLastPage = async () => {
        setCurrentPage(totalPages);
        await onPagination(totalPages, pageSize);
    };

    const startIndex = (currentPage - 1) * pageSize + 1;
    const endIndex = Math.min(currentPage * pageSize, totalCount) || null;

    return (
        <StyledPaginationContainer data-testid="pagination">
            <ConditionalRendering isTrue={isShowPaginationHeader}>
                <StyledDataRange data-testid="data-range" mt={margin}>
                    {startIndex}-{endIndex} of {totalCount}
                </StyledDataRange>
            </ConditionalRendering>

            <PaginationUI
                prevText={prevText}
                nextText={nextText}
                pages={pageNumbersToShow}
                onPageChange={onPageChange}
                isPrevDisabled={currentPage === 1}
                isActive={page => currentPage === page}
                isNextDisabled={currentPage === totalPages}
                onPrev={() => onPageChange(currentPage - 1)}
                onNext={() => onPageChange(currentPage + 1)}
                goToFirstPage={goToFirstPage}
                goToLastPage={goToLastPage}
                isFirstAndLastNavigation={isFirstAndLastNavigation}
            />

            <ConditionalRendering isTrue={isShowRowsPerPage}>
                <StyledBox>
                    <span>Rows</span>
                    <StyledSelect
                        data-testid="select-rows-per-page"
                        value={pageSize}
                        onChange={e => {
                            const newSize = Number(e.target.value);
                            setPageSize(newSize);
                            setCurrentPage(1);
                            onPagination(1, newSize);
                        }}
                    >
                        {rowsPerPageOptions.map((size: number) => (
                            <option key={size} value={size}>
                                {size}
                            </option>
                        ))}
                    </StyledSelect>
                </StyledBox>
            </ConditionalRendering>
        </StyledPaginationContainer>
    );
}

export default ServerSidePagination;
